<script setup lang="ts">
/**
 * /dashboard —— 监管驾驶舱(投屏主秀;v2 · G1,1920×1080 全屏,S-49)。
 *
 * ═══ 这一页没有壳(App.vue 按路由跳过)═══
 *
 * 画板把这一页画成整块 1920×1080 的大屏:没有侧栏、没有内容区留白。
 * 所以它自己就是整屏,自己挂深色的令牌变量(useToneVars,与 AppShell 同一处收口),
 * 自己画顶栏与底栏。
 *
 * ═══ 顶栏的页签:画板没画导航,这一条是人要求的补件 ═══
 *
 * G1 画板没有画任何导航件(它把这页画成投屏主秀,不预设有人会点着走)。**这一条是人
 * 明确要求的**(2026-10-18:「监管端的 dashboard 页面需要有菜单,不然点不到其他页面去」),
 * 所以是有意偏离画板、不是漏做:顶栏左区加一条四项页签(数据大屏 / 订单 / 起飞申请 /
 * 综合监控,顺序与 App.vue 的 NAV 一致,当前页高亮),四项都有真实落点。
 * **不加侧栏** —— 大屏是 1920×1080 的定版布局,塞一个侧栏会把中央列与右栏整体挤走;
 * 页签加在顶栏里,对布局的扰动最小。偏离记在 assumptions.json(AS-103)与 design-copy
 * G1 帧的 _structure。
 * 订单详情卡右上那枚「查看详情 ›」仍是页面里另一条有落点的链接(点进 G3 监管端订单详情)。
 *
 * 这一份手写页签清单与 App.vue 的 NAV 逐项对齐(标签 / to / 顺序),由 admin-nav 守卫的
 * 「页面内导航清单」判据盯着 —— 它是全仓第二处手写监管端导航的地方(第一处是 MonitorView),
 * 两处漂移在页面上都看不出异常,只是少一个入口。
 *
 * ═══ 两组数据:虚拟的船舶世界与真实的订单世界(Y-34,2026-10-16 人裁决)═══
 *
 *   虚拟(确定性固定值,全部来自 ../demo/g1-demo-data.ts,逐块 @unimplemented):
 *     左栏四张 KPI 卡(在航船舶 42 / 内锚地锚泊 82 / 外锚地锚泊 42 / 今日外轮代理单 86)、
 *     中央地图整块(群岛 / 航线 / 船位 / 走私预警区 / 视频联动卡 = 烘好的演示画布)、
 *     底栏六类事件计数(128 / 96 / 3 / 42 / 27 / 5)、「系统运行正常」、
 *     六路视频格与六部门接入(Y-33 的演示占位口径)。
 *
 *   真实(一个都不许被虚拟值顶掉 —— 这是 Y-34 的另一半):
 *     ① 右栏「订单详情」卡 = **最近的真实订单**(列表取最近一单,再取一次详情:
 *        共两个请求,不是每行 N+1;字段逐行见 orderRows 的注释);
 *     ② 顶栏东八区时钟 = 真时刻(与旧版同一个 formatShanghaiDateTime 秒级时钟);
 *     ③ 卡底那行「订单口径」= 顶栏区间上真实的单量 / 架次 / 在途(GET /api/gov/dashboard,
 *        区间 = 本月 1 号 ~ 今天,东八区 —— 顶栏左侧那行字就是它,是**真查询参数**,
 *        不是画板的示例日期)。
 *     口径说明(Y-15 的四条口径、这块屏的虚拟边界)收在订单卡的口径说明浮层里,
 *     演示时被问到点开就能答。
 *
 * ═══ 地图用烘好的演示画布,不重画 ═══
 *
 * public/g1-map-canvas.png 是从设计稿导出的整张画布(2124×1548 = 设计 1062×774 的 2x):
 * 群岛、八条航线、里程标签、仨起降场、船位、锚地图标、走私预警圈与标签、
 * 视频联动卡、「多部门联合监管区」与比例尺全烘在里面。DOM 再画一遍会与原字重影;
 * 这些字是**像素**,不可能与抄本漂移 —— 帧的 _notRequired 里逐条记着(三处)。
 *
 * ═══ 零金额 ═══
 *
 * 这一页没有任何金额字段(L0-01 / M-07):订单卡的八行逐行对照 GovOrderDetail,
 * 那里就没有金额;演示数据全是船与事件的计数。
 */
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { NConfigProvider, darkTheme, zhCN } from 'naive-ui'
import { formatShanghaiDate, formatShanghaiDateTime, formatShanghaiMinute, PROOF_NODE } from '@sea-drone/shared'
import {
  ErrorBanner,
  govThemeOverrides,
  NavIcon,
  ScopeNotes,
  statusLabel,
  StatusTag,
  useToneVars,
  type NavItem,
  type ScopeNote,
} from '@sea-drone/ui'
import { api, ApiError, type GovDashboard, type GovOrderDetail, type Proof } from '../api/client'
import {
  G1_DEMO_DEPARTMENTS,
  G1_DEMO_EVENTS,
  G1_DEMO_KPI,
  G1_DEMO_SYSTEM_STATUS,
  G1_DEMO_VIDEO_CELLS,
} from '../demo/g1-demo-data'

/** 深色令牌变量:这一页不在 AppShell 里,自己挂(与 AppShell 同一处收口,见 tone-vars.ts) */
const vars = useToneVars(() => 'dark')

// ── 顶栏页签:人要求的补件(画板没画导航,见文件头)──────────────
//
// 四项与 App.vue 的 NAV 同名同序(全仓的监管端页面清单)。写一份字面量而不是
// import App.vue —— admin-nav 守卫要求 NAV 以字面量住在 App.vue 里(它按那份清单做
// NAV ↔ 路由 ↔ 视图文件的三向核对),而这里这一栏是画在页面里的**第二处**手写导航,
// 由同一条守卫的「页面内导航清单」判据按 label / to / 顺序逐项对齐。

const route = useRoute()

const NAV_TABS: readonly NavItem[] = [
  { key: 'dashboard', label: '数据大屏', to: '/dashboard', icon: 'dashboard' },
  { key: 'orders', label: '订单', to: '/orders', icon: 'list' },
  { key: 'takeoff-applications', label: '起飞申请', to: '/takeoff-applications', icon: 'clipboard' },
  { key: 'monitor', label: '综合监控', to: '/monitor', icon: 'monitor' },
]

/** 当前页高亮。与 AppShell / MonitorView 同口径的精确匹配(本页没有带参数的子路由) */
function isCurrent(to: string): boolean {
  return route.path === to
}

// ── 顶栏:时钟与区间 ─────────────────────────────────────────

/**
 * 东八区实时时钟。时刻一律走 formatShanghaiDateTime(Y-03),
 * 不是 new Date().toLocaleString() —— 那一个在东八区之外会差 8 小时,
 * 而大屏看上去完全正常。取后 8 位就是时分秒。
 */
const now = ref(new Date())
let clockTimer: ReturnType<typeof setInterval> | null = null
const clockText = computed(() => formatShanghaiDateTime(now.value).slice(11))

/**
 * 数据区间 = 本月 1 号 ~ 今天(东八区)—— 顶栏左侧那行字。
 * 它同时是订单口径三个数的**真查询参数**(dashboard 的 from/to),
 * 所以挂载时定下来、页面存续期间不再变:文字与数出同一处,不会对不上。
 * 画板上那行「2026-10-01 至 2026-10-15」是画板当天算出来的示例值,数据不锁。
 */
const rangeFrom = ref('')
const rangeTo = ref('')
const rangeText = computed(() => `${rangeFrom.value} 至 ${rangeTo.value}`)

// ── 两组数据 ─────────────────────────────────────────────────

const dashboard = ref<GovDashboard | null>(null)
const statsError = ref<string | null>(null)
const recentOrder = ref<GovOrderDetail | null>(null)
const orderError = ref<string | null>(null)

/** 统一那句错话:`[CODE] 消息（requestId xxx）`(client.ts 拼的),不许吞成一句「加载失败」 */
function errorText(e: unknown): string {
  if (e instanceof ApiError) return `[${e.code}] ${e.message}（requestId ${e.requestId}）`
  return e instanceof Error ? e.message : String(e)
}

/**
 * 订单口径的三个真数(单量 / 架次 / 在途)。失败就把原句留在页脚 ——
 * 页脚画「—」的话,演示现场读起来与「今天一单没有」一模一样。
 */
async function loadStats(): Promise<void> {
  try {
    dashboard.value = await api.dashboard({ from: rangeFrom.value, to: rangeTo.value })
    statsError.value = null
  } catch (e) {
    dashboard.value = null
    statsError.value = errorText(e)
  }
}

/**
 * 最近的真实订单。两个请求:列表(pageSize=1,服务端按 createdAt 倒序)拿最近一单,
 * 再取一次详情拿飞手与凭证 —— **不是每行 N+1**(八行里只有「承接飞手 / 起飞时间 /
 * 凭证进度」需要详情,一次详情全答)。
 */
async function loadOrder(): Promise<void> {
  try {
    const page = await api.listOrders({ page: 1, pageSize: 1 })
    const first = page.items[0]
    if (first === undefined) {
      recentOrder.value = null
      orderError.value = null
      return
    }
    recentOrder.value = await api.getOrder(first.orderNo)
    orderError.value = null
  } catch (e) {
    recentOrder.value = null
    orderError.value = errorText(e)
  }
}

// ── 订单详情卡:八行(画板行序与行名逐字)────────────────────
//
//   状态     = status(中文走 statusLabel 单点,chip 走 StatusTag)
//   路线     = originArea → destinationArea(区域;空画「未标注」)
//   距离     = 契约里没有 distanceKm → 「—」(见下面那条 @unimplemented)
//   承运商   = carrierName;承接飞手 = pilotName(空画「—」)
//   下单时间 = createdAt(东八区、只到分钟;Y-03)
//   起飞时间 = 起飞那张凭证的时刻(takenAt 优先、退 uploadedAt;先例 AS-080③)
//   凭证进度 = 已留证节点数 / PROOF_NODE 长度(分母不写死 4)
//
// @unimplemented(reason="订单详情卡的「距离」行在契约里没有对应字段 —— GovOrderDetail 只有区域、承运商、飞手与凭证,没有 distanceKm(与 G3 监管端订单详情同一条)。拿区域间直线距离现算是一个编出来的数,按「拿不到就画破折号」处理。要它先给契约加字段", owner="me", blocks="G1 订单详情卡「距离」行")

const provedCount = computed(() => recentOrder.value?.proofs.length ?? 0)

/** 一切「N / 4 已留证」的写法只有这一处(与 G3 / M15 同一形态,分母走共享包) */
const proofProgressText = computed(() => `${provedCount.value} / ${PROOF_NODE.length} 已留证`)

/** 起飞时间 = 起飞那张凭证的时刻(takenAt 优先、退 uploadedAt,东八区只到分钟;AS-080③) */
const takeoffText = computed(() => {
  const p: Proof | undefined = recentOrder.value?.proofs.find((x) => x.node === 'took_off')
  if (p === undefined) return null
  return formatShanghaiMinute(p.takenAt ?? p.uploadedAt)
})

const orderRows = computed(() => {
  const o = recentOrder.value
  if (o === null) return []
  return [
    { label: '状态', value: null, isStatus: true },
    { label: '路线', value: `${o.originArea ?? '未标注'} → ${o.destinationArea ?? '未标注'}`, isStatus: false },
    { label: '距离', value: null, isStatus: false },
    { label: '承运商', value: o.carrierName, isStatus: false },
    { label: '承接飞手', value: o.pilotName, isStatus: false },
    { label: '下单时间', value: formatShanghaiMinute(o.createdAt), isStatus: false },
    { label: '起飞时间', value: takeoffText.value, isStatus: false },
    { label: '凭证进度', value: proofProgressText.value, isStatus: false },
  ]
})

/** 页脚那行:订单口径的三个真数(顶栏那个区间上的) */
const statsFootText = computed(() => {
  if (statsError.value !== null) return `订单口径取数失败:${statsError.value}`
  const d = dashboard.value
  if (d === null) return ''
  return `区间口径 · 单量 ${d.orderCount} 单 · 架次 ${d.flightCount} · 此刻在途 ${d.inTransitCount} 单`
})

/** 口径说明浮层(Y-15 的四条口径、虚拟边界 Y-34、视频占位 Y-33)—— 演示时点开就能答 */
const CARD_NOTES: readonly ScopeNote[] = [
  {
    title: '订单口径(页脚那三个数,也是 Y-15)',
    body:
      '单量 = 区间内下单的单数;架次 = 区间内真的起飞过的单数(有起飞凭证才算);' +
      '在途 = 此刻已接单、尚未送达 —— 这一条不受区间影响。区间就是顶栏左侧那行(本月 1 号 ~ 今天,东八区)。' +
      '区域分布按目的地区域分组,没填的归「未标注」桶,这条路要数的话在接口里(byArea)。',
    refs: ['Y-15', 'Y-03'],
  },
  {
    title: '这块屏上哪些是演示数据、哪些是真数(Y-34)',
    body:
      '左栏四张卡、中央地图、底栏六类事件、「系统运行正常」是演示虚拟数据(确定性固定值,不接真实数据源 —— ' +
      '船舶世界在本平台没有数据模型,甲方口径待给出)。真实数据只有三处:这张卡的订单、右上角的东八区时钟、' +
      '页脚那三个订单口径的数 —— 它们一个都不会被演示数据顶掉。',
    refs: ['Y-34'],
  },
  {
    title: '视频与监管仓是演示占位(Y-33)',
    body:
      '按甲方 2026-10-14 口径:六个部门接入位与六路视频格是演示占位,不接真实视频流与真实系统' +
      '(视频整块不在本期:L0-06 搁置、M-09 排除、BLK-02 未解除)。地图上那张视频联动卡烘在演示底图里。',
    refs: ['Y-33', 'L0-06', 'M-09'],
  },
]

// ── 左栏 KPI / 底栏事件 / 右栏部门:名字与数值 ────────────────
//
// 名字(画板逐字)住在这一页;数值来自演示数据模块(Y-34),逐个对应。

const kpiCards = [
  { key: 'inVoyage', icon: 'ship', label: '在航船舶 · 艘', value: G1_DEMO_KPI.inVoyage, caption: '此刻在舟山海域航行的船舶', bar: 'accent' },
  { key: 'innerAnchorage', icon: 'anchor', label: '内锚地锚泊 · 艘', value: G1_DEMO_KPI.innerAnchorage, caption: '内锚地待泊与作业中的船舶', bar: 'accent' },
  { key: 'outerAnchorage', icon: 'anchor', label: '外锚地锚泊 · 艘', value: G1_DEMO_KPI.outerAnchorage, caption: '外锚地锚泊、需无人机送件的船舶', bar: 'warning' },
  { key: 'agencyToday', icon: 'clipboard', label: '今日外轮代理单 · 单', value: G1_DEMO_KPI.agencyToday, caption: '船员证书与物料送上锚轮、边检盖章', bar: 'accent' },
] as const

/**
 * 顶栏那行健康状态的字。状态位来自演示模块(Y-34,虚拟),字住在这里(文案)。
 * 中文常量写在 script 里而不是模板的插值表达式里:design-copy 的比对会先去掉
 * `{{ … }}`(它渲染的是数据不是写死的字),写在插值里的文案守卫看不见。
 */
const systemStatusText = G1_DEMO_SYSTEM_STATUS === 'normal' ? '系统运行正常' : '未知状态'

/** 地图图例。「运输中」= 订单状态的中文名(单点定义在 status.ts),页面里不手写这三个字 */
const legend = [
  { key: 'transit', label: statusLabel('in_transit'), tone: 'success' },
  { key: 'ready', label: '准备运输', tone: 'warning' },
  { key: 'alert', label: '预警异常', tone: 'danger' },
  { key: 'idle', label: '不在飞', tone: 'muted' },
] as const

const eventCards = [
  { key: 'ship', label: '船舶事件', tone: 'info', count: G1_DEMO_EVENTS.ship },
  { key: 'videoMonitor', label: '视频监管事件', tone: 'info', count: G1_DEMO_EVENTS.videoMonitor },
  { key: 'smuggling', label: '走私监管预警', tone: 'danger', count: G1_DEMO_EVENTS.smuggling },
  { key: 'customs', label: '海关验放存档', tone: 'success', count: G1_DEMO_EVENTS.customs },
  { key: 'immigration', label: '边检登轮记录', tone: 'success', count: G1_DEMO_EVENTS.immigration },
  { key: 'aisAlarm', label: 'AIS 信号告警', tone: 'warning', count: G1_DEMO_EVENTS.aisAlarm },
] as const

/**
 * 部门名(画板逐字)+ 演示数据模块给的灯色。
 * 查不到的键显示机器键本身(与 statusLabel 同一做法:让它刺眼,而不是编一个名字)。
 */
const DEPARTMENT_NAMES: Record<string, string> = {
  msa: '海事',
  customs: '海关',
  immigration: '边检',
  port: '港航',
  police: '公安',
  emergency: '应急',
}
const departments = G1_DEMO_DEPARTMENTS.map((d) => ({ ...d, name: DEPARTMENT_NAMES[d.key] ?? d.key }))

/**
 * 六路直播格:把演示模块里的小写机器取值('live' / 'rec')翻成画板上那两个词。
 * **两个词住在这里**(页面文案),演示模块里只有机器取值 —— 数据与文案各归各的。
 */
const videoCells = G1_DEMO_VIDEO_CELLS.map((cell) => ({
  ...cell,
  badgeText: cell.badge === 'live' ? 'LIVE' : 'REC',
  badgeClass: cell.badge === 'live' ? 'video-badge--live' : 'video-badge--rec',
}))

onMounted(() => {
  rangeTo.value = formatShanghaiDate(new Date())
  rangeFrom.value = `${rangeTo.value.slice(0, 8)}01`
  clockTimer = setInterval(() => {
    now.value = new Date()
  }, 1000)
  void loadStats()
  void loadOrder()
})

// 不清理就是每秒一次的内存泄漏 —— 路由切走之后这个定时器还在跑
onUnmounted(() => {
  if (clockTimer !== null) {
    clearInterval(clockTimer)
    clockTimer = null
  }
})
</script>

<template>
  <n-config-provider :theme="darkTheme" :theme-overrides="govThemeOverrides" :locale="zhCN">
    <div class="screen" :style="vars">
      <div class="stage">
        <!-- 装饰:外框 + 画面四角角标(照画板手画,不加依赖) -->
        <span class="stage-frame" aria-hidden="true" />
        <span class="stage-corner stage-corner--tl" aria-hidden="true" />
        <span class="stage-corner stage-corner--tr" aria-hidden="true" />
        <span class="stage-corner stage-corner--bl" aria-hidden="true" />
        <span class="stage-corner stage-corner--br" aria-hidden="true" />

        <!-- ── 顶栏 ─────────────────────────────────────── -->
        <header class="topbar">
          <div class="tb-left">
            <div class="tb-brand">
              <span class="brand-bar" aria-hidden="true" />
              <div class="tb-text">
                <span class="tb-range">{{ rangeText }}</span>
                <span class="tb-status">{{ systemStatusText }}</span>
              </div>
            </div>

            <!-- 页签 = 人要求的补件(画板没画导航,见文件头);四项与 App.vue 的 NAV 同名同序 -->
            <nav class="tabs" aria-label="页面">
              <router-link
                v-for="t in NAV_TABS"
                :key="t.key"
                class="tab"
                :class="{ 'tab--active': isCurrent(t.to) }"
                :to="t.to"
              >
                {{ t.label }}
              </router-link>
            </nav>
          </div>

          <div class="tb-center">
            <span class="crest" aria-hidden="true" />
            <h1 class="screen-title">舟山内外锚地船舶物流一体化监管驾驶舱</h1>
            <span class="crest crest--flip" aria-hidden="true" />
          </div>

          <div class="tb-right">
            <div class="clock-block">
              <span class="clock-time">{{ clockText }}</span>
              <span class="clock-zone">东八区</span>
            </div>
            <span class="readonly-badge">全程只读</span>
          </div>

          <!-- 装饰 · 顶栏光条(两端渐隐) -->
          <span class="tb-glow" aria-hidden="true" />
        </header>

        <!-- ── 主区:左栏 / 地图 / 右栏 ────────────────── -->
        <main class="main">
          <section class="rail rail--left">
            <article v-for="card in kpiCards" :key="card.key" class="kpi-card">
              <span class="brackets" aria-hidden="true" />
              <div class="kpi-head">
                <!-- 船 / 锚 / 凭证夹三只图形改调共享图标表(S-50:G5 的船舶列表第二次用到船与锚,
                     内联副本按单点定义纪律收进 packages/ui/src/icons.ts,形状逐点相同) -->
                <NavIcon class="kpi-icon" :name="card.icon" :size="20" />
                <span class="kpi-label">{{ card.label }}</span>
                <span class="kpi-bar" :class="`kpi-bar--${card.bar}`" aria-hidden="true" />
                <svg class="kpi-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true">
                  <path d="M9.5 6l6 6-6 6" />
                </svg>
              </div>
              <div class="kpi-value">{{ card.value }}</div>
              <p class="kpi-caption">{{ card.caption }}</p>
            </article>
          </section>

          <section class="map-panel">
            <span class="brackets" aria-hidden="true" />
            <header class="map-head">
              <div class="map-title">
                <h2>实时航迹</h2>
                <span class="map-sub">舟山海域 · 内外锚地 · 多部门并联</span>
              </div>
              <ul class="legend">
                <li v-for="item in legend" :key="item.key" class="legend-item">
                  <i class="dot" :class="`dot--${item.tone}`" aria-hidden="true" />
                  <span>{{ item.label }}</span>
                </li>
              </ul>
            </header>
            <!--
              地图整块 = 从设计稿导出的烘好的演示画布(见文件头):船位 / 航线 /
              预警圈 / 视频联动卡 / 地名与里程标签都在图里,不重画 DOM。
            -->
            <div class="map-body">
              <img
                class="map-canvas"
                src="/g1-map-canvas.png"
                alt="舟山海域演示底图:群岛、航线、船位与走私监管预警区(演示数据,非实况)"
              />
            </div>
          </section>

          <section class="rail rail--right">
            <!-- 多部门监管仓(Y-33 演示占位;口径小字是画板逐字,给甲方看的) -->
            <article class="warehouse-card">
              <span class="brackets" aria-hidden="true" />
              <h3 class="card-title">多部门监管仓</h3>
              <p class="warehouse-note">海事、海关、边检、港航、公安、应急统一接入;以下画面为演示占位,实现时接虚拟数据源。</p>
              <div class="dept-row">
                <span v-for="d in departments" :key="d.key" class="dept-chip">
                  <i class="dept-dot" :class="`dept-dot--${d.tone}`" aria-hidden="true" />{{ d.name }}
                </span>
              </div>
              <div class="video-grid">
                <figure v-for="cell in videoCells" :key="cell.ch" class="video-cell">
                  <img class="video-img" :src="`/${cell.img}`" :alt="`${cell.point} 演示画面(非实况)`" />
                  <span class="video-scrim" aria-hidden="true" />
                  <figcaption class="video-top">
                    <span class="video-badge" :class="cell.badgeClass">
                      <i class="video-badge-dot" aria-hidden="true" />{{ cell.badgeText }}
                    </span>
                    <span class="video-ch">{{ cell.ch }}</span>
                  </figcaption>
                  <span class="video-point">{{ cell.point }}</span>
                </figure>
              </div>
            </article>

            <!-- 订单详情卡 = 最近的真实订单(整块屏上唯一的订单世界,不许被演示数据顶掉) -->
            <article class="order-card">
              <header class="order-head">
                <h3 class="card-title">订单详情</h3>
                <ScopeNotes tone="dark" :notes="CARD_NOTES" />
                <router-link v-if="recentOrder" class="order-link" :to="`/orders/${recentOrder.orderNo}`">查看详情 ›</router-link>
              </header>

              <ErrorBanner v-if="orderError" tone="dark" title="订单取数失败" :message="orderError" />

              <template v-else-if="recentOrder">
                <dl class="order-rows">
                  <div v-for="row in orderRows" :key="row.label" class="order-row">
                    <dt>{{ row.label }}</dt>
                    <dd>
                      <StatusTag v-if="row.isStatus" tone="dark" size="sm" :status="recentOrder.status" />
                      <span v-else class="row-value">{{ row.value ?? '—' }}</span>
                    </dd>
                  </div>
                </dl>
                <!-- 页脚:订单口径的三个真数(顶栏那个区间上的,取数失败就把原句留在这里) -->
                <p class="order-foot" :class="{ 'order-foot--error': statsError !== null }">{{ statsFootText }}</p>
              </template>

              <p v-else class="order-empty">库里还没有订单 —— 这一格显示全平台最近的一单。</p>
            </article>
          </section>
        </main>

        <!-- ── 底栏:实时事件 ───────────────────────────── -->
        <footer class="bottombar">
          <div class="events-head">
            <span class="events-title">实时事件</span>
            <span class="events-sub">全部部门</span>
          </div>
          <article v-for="ev in eventCards" :key="ev.key" class="event-card">
            <i class="event-dot" :class="`event-dot--${ev.tone}`" aria-hidden="true" />
            <div class="event-text">
              <span class="event-name">{{ ev.label }}</span>
              <span class="event-count" :class="`event-count--${ev.tone}`">{{ ev.count }} 条</span>
            </div>
          </article>
        </footer>
      </div>
    </div>
  </n-config-provider>
</template>

<style scoped>
/* ═══ 整屏与舞台 ═══════════════════════════════════════
   固定定位铺满视口(body 有默认外边距,固定定位不受它影响);
   舞台锁 1920×1080(投屏目标),视口更小时整体滚动,不禁缩放。 */
.screen {
  position: fixed;
  inset: 0;
  overflow: auto;
  background: var(--sd-bg-deep);
  color: var(--sd-text1);
  font-family: var(--sd-font-sans);
}

.stage {
  position: relative;
  width: 1920px;
  height: 1080px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  font-variant-numeric: tabular-nums;
}

/* 大屏里所有盒子按 border-box 算:画板上的宽高都是**含内边距**的(画板工具皆如此),
   默认的 content-box 会把「513 高、内衬 20」的卡撑成 555 —— 页面看上去只是
   「这一块比画板胖一点」,而它下面那张卡会被挤出屏幕底(实测过一次)。
   带着 [data-v] 的 scoped 作用域只盖到本文件的元素,不动共享组件内部。 */
.stage,
.stage * {
  box-sizing: border-box;
}

/* 装饰 · 外框(画板:1900×1060 @(10,10),1px $gov-border) */
.stage-frame {
  position: absolute;
  inset: 10px;
  border: 1px solid var(--sd-border);
  pointer-events: none;
  z-index: 3;
}

/* 装饰 · 画面四角角标(26×26 的两条边,accent) */
.stage-corner {
  position: absolute;
  width: 26px;
  height: 26px;
  border: 1px solid var(--sd-accent);
  pointer-events: none;
  z-index: 3;
}

.stage-corner--tl { top: 0; left: 0; border-right: none; border-bottom: none; }
.stage-corner--tr { top: 0; right: 0; border-left: none; border-bottom: none; }
.stage-corner--bl { bottom: 0; left: 0; border-right: none; border-top: none; }
.stage-corner--br { bottom: 0; right: 0; border-left: none; border-top: none; }

/* ═══ 顶栏(高 104)═══════════════════════════════════ */
.topbar {
  position: relative;
  flex: none;
  height: 104px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 0 24px;
  /* 底纹(60px 网格)+ 整条的渐深(画板:#13223D → #0A1120,这里取同值的令牌档) */
  background-image:
    repeating-linear-gradient(90deg, var(--sd-border) 0 1px, transparent 1px 60px),
    repeating-linear-gradient(0deg, var(--sd-border) 0 1px, transparent 1px 60px),
    linear-gradient(180deg, var(--sd-surface-alt) 0%, var(--sd-bg) 100%);
}

/* 装饰 · 顶栏光条(y=102,两端渐隐的 accent) */
.tb-glow {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 2px;
  height: 2px;
  background: linear-gradient(90deg, transparent 0%, var(--sd-accent) 50%, transparent 100%);
  opacity: 0.6;
}

.tb-left {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  min-width: 300px;
}

.tb-brand {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* 页签:画板没画导航,这一条是人要求的补件(见文件头)——
   写法与样式照 MonitorView 顶栏那条页签(激活 = 强调色字 + 56×2 下划线)。
   这里间距收窄到 32:大屏顶栏中央有大标题,不像 G5 那样整条都给页签。 */
.tabs {
  display: flex;
  align-items: center;
  gap: 32px;
}

.tab {
  position: relative;
  font-size: 14px;
  color: var(--sd-text2);
  text-decoration: none;
  white-space: nowrap;
  padding: 6px 0;
}

.tab--active {
  color: var(--sd-accent);
}

.tab--active::after {
  content: '';
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: -4px;
  width: 56px;
  height: 2px;
  background: var(--sd-accent);
}

/* 品牌块:8×36 的 accent 竖条 */
.brand-bar {
  flex: none;
  width: 8px;
  height: 36px;
  border-radius: var(--sd-radius-sm);
  background: var(--sd-accent);
}

.tb-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tb-range {
  font-size: 14px;
  color: var(--sd-text2);
}

.tb-status {
  font-size: 13px;
  color: var(--sd-success);
}

.tb-center {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
}

.screen-title {
  margin: 0;
  font-size: 34px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: var(--sd-text1);
  white-space: nowrap;
}

/* 纹样 · 两侧:一条渐隐的短线 + 一枚 12×12 菱形 */
.crest {
  flex: none;
  width: 110px;
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--sd-accent));
  position: relative;
}

.crest::after {
  content: '';
  position: absolute;
  right: -8px;
  top: -5px;
  width: 12px;
  height: 12px;
  background: var(--sd-accent);
  transform: rotate(45deg);
}

.crest--flip {
  background: linear-gradient(270deg, transparent, var(--sd-accent));
}

.crest--flip::after {
  right: auto;
  left: -8px;
}

.tb-right {
  display: flex;
  align-items: center;
  gap: 20px;
}

.clock-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.clock-time {
  font-size: 60px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.03em;
  color: var(--sd-accent);
}

.clock-zone {
  font-size: 13px;
  color: var(--sd-text3);
}

/* 只读徽标(画板:$gov-tag-draft 一族 —— 灰蓝底 + 描边 + 灰蓝字) */
.readonly-badge {
  flex: none;
  padding: 8px;
  border-radius: var(--sd-radius-sm);
  border: 1px solid var(--sd-border-strong);
  background: var(--sd-surface-alt);
  font-size: 14px;
  color: var(--sd-text2);
  white-space: nowrap;
}

/* ═══ 主区(顶栏与底栏之间,内衬 14)═══════════════════ */
.main {
  flex: 1;
  min-height: 0;
  display: flex;
  gap: 14px;
  padding: 14px;
}

.rail {
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: none;
}

.rail--left { width: 300px; }
.rail--right { width: 470px; }

/* ── 左栏:KPI 卡 ───────────────────────────────────── */

.kpi-card {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px;
  border-radius: var(--sd-radius-sm);
  border: 1px solid var(--sd-border);
  background: linear-gradient(160deg, var(--sd-surface-alt) 0%, var(--sd-bg) 100%);
}

.kpi-head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.kpi-icon {
  flex: none;
  width: 20px;
  height: 20px;
  color: var(--sd-accent);
}

.kpi-label {
  font-size: 17px;
  color: var(--sd-text2);
  white-space: nowrap;
}

/* 行 1 右端:一条 4×22 的色条 + 右箭头(画板) */
.kpi-bar {
  margin-left: auto;
  flex: none;
  width: 4px;
  height: 22px;
  border-radius: var(--sd-radius-pill);
}

.kpi-bar--accent { background: var(--sd-accent); }
.kpi-bar--warning { background: var(--sd-warning); }

.kpi-chevron {
  flex: none;
  width: 18px;
  height: 18px;
  color: var(--sd-text3);
}

.kpi-value {
  font-size: 72px;
  font-weight: 700;
  line-height: 1.1;
  color: var(--sd-text1);
}

.kpi-caption {
  margin: auto 0 0;
  font-size: 12px;
  color: var(--sd-text3);
}

/* ── 中央:地图面板 ─────────────────────────────────── */

.map-panel {
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-radius: var(--sd-radius-sm);
  border: 1px solid var(--sd-border);
  background: var(--sd-surface);
}

.map-head {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.map-title {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.map-title h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--sd-text1);
}

.map-sub {
  font-size: 12px;
  color: var(--sd-text2);
}

.legend {
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: var(--sd-text2);
  white-space: nowrap;
}

.dot {
  width: 10px;
  height: 10px;
  flex: none;
  border-radius: var(--sd-radius-pill);
}

.dot--success { background: var(--sd-success); }
.dot--warning { background: var(--sd-warning); }
.dot--danger { background: var(--sd-danger); }
.dot--muted { background: var(--sd-text3); }

.map-body {
  flex: 1;
  min-height: 0;
  display: flex;
  justify-content: center;
}

/* 画布原生 2x(2124×1548):按设计宽 1062 铺,清晰度不丢 */
.map-canvas {
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
  object-position: top center;
}

/* ── 右栏:多部门监管仓(高 513,画板)───────────────── */

.warehouse-card {
  position: relative;
  flex: none;
  height: 513px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  border-radius: var(--sd-radius-sm);
  border: 1px solid var(--sd-border);
  background: var(--sd-surface);
  overflow: hidden;
}

.card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--sd-text1);
}

.warehouse-note {
  margin: 0;
  font-size: 11px;
  line-height: 1.7;
  color: var(--sd-text3);
}

.dept-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.dept-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: var(--sd-radius-sm);
  border: 1px solid var(--sd-border);
  background: var(--sd-surface-alt);
  font-size: 12px;
  color: var(--sd-text2);
}

.dept-dot {
  width: 5px;
  height: 5px;
  flex: none;
  border-radius: var(--sd-radius-pill);
}

.dept-dot--ok { background: var(--sd-success); }
.dept-dot--warn { background: var(--sd-warning); }

/* 直播流矩阵:两行 × 三格,格内实拍图铺满 + 顶行徽标/编号 + 底部点位名 */
.video-grid {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 8px;
}

.video-cell {
  position: relative;
  margin: 0;
  overflow: hidden;
  border-radius: var(--sd-radius-sm);
  border: 1px solid var(--sd-border);
  background: var(--sd-bg-deep);
}

.video-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 顶部与底部各压一条渐隐的暗底:徽标与点位名要压在实拍图上,得读得清 */
.video-scrim {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, var(--sd-bg-deep) 0%, transparent 32%, transparent 55%, var(--sd-bg-deep) 100%);
  opacity: 0.72;
}

.video-top {
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.video-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 1px 6px;
  border-radius: var(--sd-radius-sm);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
}

.video-badge--live {
  background: var(--sd-danger-bg);
  border: 1px solid var(--sd-danger-border);
  color: var(--sd-danger);
}

.video-badge--rec {
  background: var(--sd-warning-bg);
  border: 1px solid var(--sd-warning-border);
  color: var(--sd-warning);
}

.video-badge-dot {
  width: 5px;
  height: 5px;
  flex: none;
  border-radius: var(--sd-radius-pill);
  background: currentColor;
}

.video-ch {
  font-size: 10px;
  color: var(--sd-text3);
}

.video-point {
  position: absolute;
  left: 8px;
  bottom: 6px;
  font-size: 10px;
  color: var(--sd-text2);
}

/* ── 右栏:订单详情卡(尾高,画板 317)──────────────── */

.order-card {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  border-radius: var(--sd-radius-sm);
  border: 1px solid var(--sd-border);
  background: var(--sd-surface);
  overflow: auto;
}

.order-head {
  flex: none;
  display: flex;
  align-items: center;
  gap: 12px;
}

.order-link {
  margin-left: auto;
  font-size: 13px;
  color: var(--sd-accent);
  text-decoration: none;
  white-space: nowrap;
}

.order-link:hover {
  text-decoration: underline;
}

/* 八行按 space-between 均摊卡内余高(画板的行距就是均摊的),不给固定 gap:
   卡高是画板定的 317,行距写死会在某些字号下把最后一行顶出卡外 */
.order-rows {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 0;
  margin: 0;
}

.order-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.order-row dt {
  font-size: 13px;
  color: var(--sd-text3);
  white-space: nowrap;
}

.order-row dd {
  margin: 0;
  font-size: 13px;
  color: var(--sd-text1);
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.row-value {
  font-variant-numeric: tabular-nums;
}

/* 页脚:订单口径的三个真数(整块屏上唯一的订单世界指标) */
.order-foot {
  flex: none;
  margin: 0;
  font-size: 11px;
  color: var(--sd-text3);
  border-top: 1px solid var(--sd-border);
  padding-top: 8px;
}

.order-foot--error {
  color: var(--sd-danger);
}

.order-empty {
  margin: 0;
  font-size: 12px;
  color: var(--sd-text3);
}

/* ── 装饰 · 面板四角小角框(accent L 形,画板)────────── */

.brackets {
  position: absolute;
  inset: 6px;
  pointer-events: none;
  background-image:
    linear-gradient(var(--sd-accent), var(--sd-accent)),
    linear-gradient(var(--sd-accent), var(--sd-accent)),
    linear-gradient(var(--sd-accent), var(--sd-accent)),
    linear-gradient(var(--sd-accent), var(--sd-accent)),
    linear-gradient(var(--sd-accent), var(--sd-accent)),
    linear-gradient(var(--sd-accent), var(--sd-accent)),
    linear-gradient(var(--sd-accent), var(--sd-accent)),
    linear-gradient(var(--sd-accent), var(--sd-accent));
  background-repeat: no-repeat;
  background-size: 12px 1px, 1px 12px, 12px 1px, 1px 12px, 12px 1px, 1px 12px, 12px 1px, 1px 12px;
  background-position:
    left top, left top,
    right top, right top,
    left bottom, left bottom,
    right bottom, right bottom;
}

/* ═══ 底栏(高 104)═══════════════════════════════════ */
.bottombar {
  position: relative;
  flex: none;
  height: 104px;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 24px;
  background-image:
    repeating-linear-gradient(90deg, var(--sd-border) 0 1px, transparent 1px 60px),
    repeating-linear-gradient(0deg, var(--sd-border) 0 1px, transparent 1px 60px),
    linear-gradient(180deg, transparent 0%, var(--sd-bg) 100%);
  background-color: var(--sd-bg);
}

.events-head {
  flex: none;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-right: 8px;
}

.events-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--sd-text1);
}

.events-sub {
  font-size: 11px;
  color: var(--sd-accent);
}

.event-card {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  height: 62px;
  padding: 12px;
  border-radius: var(--sd-radius-sm);
  border: 1px solid var(--sd-border);
  background: var(--sd-surface);
}

.event-dot {
  width: 6px;
  height: 6px;
  flex: none;
  border-radius: var(--sd-radius-pill);
}

.event-dot--info { background: var(--sd-info); }
.event-dot--success { background: var(--sd-success); }
.event-dot--warning { background: var(--sd-warning); }
.event-dot--danger { background: var(--sd-danger); }

.event-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.event-name {
  font-size: 13px;
  color: var(--sd-text1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.event-count {
  font-size: 12px;
  font-weight: 700;
}

.event-count--info { color: var(--sd-info); }
.event-count--success { color: var(--sd-success); }
.event-count--warning { color: var(--sd-warning); }
.event-count--danger { color: var(--sd-danger); }
</style>
